import React, { useState, useMemo, useEffect } from "react";
import axios from "axios";
import UseToast from "../UseToast";
import { useParams, useSearchParams } from "react-router-dom";
import { getLanguageText } from "../../utils/multilanguageUtils";
import useCurrentLanguage from "../../hooks/useCurrentLanguage";

export default function OrderSelector({
  type = "location", // 'location', 'category', 'subcategory'
  brandColor,
  title,
  searchPlaceholder,
  itemIcon = "location",
}) {
  const { userId, parentId, parentType, categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const buttonId = searchParams.get('buttonId');
  const primaryColor = brandColor || "#6366f1";
  const [searchQuery, setSearchQuery] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [orderedItems, setOrderedItems] = useState([]);

  // Get current language for multilanguage support
  const { currentLanguage } = useCurrentLanguage(userId);

  const { showSuccessToast, showWarningToast } = UseToast();

  // Configuration based on type
  const config = useMemo(() => {
    // Build fetch endpoint with buttonId if provided
    const buildFetchEndpoint = (baseEndpoint) => {
      if (buttonId) {
        return `${baseEndpoint}?buttonId=${buttonId}`;
      }
      return baseEndpoint;
    };

    const configs = {
      location: {
        title: title || "Order Locations",
        searchPlaceholder: searchPlaceholder || "Search locations...",
        selectedLabel: "Selected Locations",
        availableLabel: "Available Locations",
        emptyMessage: "No locations available",
        emptyHint:
          "Please add some locations first or check if the userId is correct",
        loadingMessage: "Loading locations...",
        icon: "location",
        fetchEndpoint:
          parentId && parentType
            ? `/api/parentLocationOrder/get-all-locations-for-parent-ordering/${userId}/${parentId}/${parentType}`
            : buildFetchEndpoint(`/api/locationOrder/get-all-locations-for-ordering/${userId}`),
        saveEndpoint:
          parentId && parentType
            ? "/api/parentLocationOrder/parent-location-order"
            : "/api/locationOrder/location-order",
        dataKey: "locationData",
        idKey: "locationId",
      },
      category: {
        title: title || "Order Categories",
        searchPlaceholder: searchPlaceholder || "Search categories...",
        selectedLabel: "Selected Categories",
        availableLabel: "Available Categories",
        emptyMessage: "No categories available",
        emptyHint: "Please add some categories first",
        loadingMessage: "Loading categories...",
        icon: "category",
        fetchEndpoint: buildFetchEndpoint(`/api/categoryOrder/get-all-categories-for-ordering/${userId}`),
        saveEndpoint: "/api/categoryOrder/category-order",
        dataKey: "categoryData",
        idKey: "categoryId",
      },
      subcategory: {
        title: title || "Order Subcategories",
        searchPlaceholder: searchPlaceholder || "Search subcategories...",
        selectedLabel: "Selected Subcategories",
        availableLabel: "Available Subcategories",
        emptyMessage: "No subcategories available",
        emptyHint: "Please add some subcategories first",
        loadingMessage: "Loading subcategories...",
        icon: "subcategory",
        fetchEndpoint: `/api/subcategoryOrder/get-all-subcategories-for-ordering/${userId}/${categoryId}`,
        saveEndpoint: "/api/subcategoryOrder/subcategory-order",
        dataKey: "subcategoryData",
        idKey: "subcategoryId",
      },
    };
    return configs[type] || configs.location;
  }, [
    type,
    title,
    searchPlaceholder,
    userId,
    parentId,
    parentType,
    categoryId,
    buttonId,
  ]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setIsLoading(true);
        console.log(`🔄 OrderSelector: Fetching ${type}s for userId:`, userId);

        const res = await axios.get(config.fetchEndpoint);
        console.log(`📡 OrderSelector: API Response for ${type}:`, res.data);

        if (res.data.success && res.data.data) {
          const data = res.data.data;
          let itemsArray = [];

          if (data[config.dataKey] && Array.isArray(data[config.dataKey])) {
            itemsArray = data[config.dataKey]
              .filter((item) => item[config.idKey])
              .map((item) => ({
                id: item[config.idKey]._id || item[config.idKey],
                name: getLanguageText(item[config.idKey].name, currentLanguage),
                buttonText: getLanguageText(item[config.idKey].buttonText, currentLanguage),
                photo:
                  type === "location"
                    ? item[config.idKey].photo ||
                      item[config.idKey].uploadedUrls?.locationPhoto?.fileUrl
                    : null,
                order: item.order,
              }));
          } else if (Array.isArray(data)) {
            itemsArray = data.map((item) => ({
              id: item._id || item.id,
              name: getLanguageText(item.name, currentLanguage),
              buttonText: getLanguageText(item.buttonText, currentLanguage) || getLanguageText(item.name, currentLanguage),
              photo:
                type === "location"
                  ? item.photo || item.uploadedUrls?.locationPhoto?.fileUrl
                  : null,
              order: item.order || null,
            }));
          }

          console.log(
            `🏢 OrderSelector: Processed ${type}s:`,
            itemsArray.length,
          );

          const sorted = [...itemsArray].sort((a, b) => {
            if (a.order !== null && b.order !== null) return a.order - b.order;
            if (a.order !== null) return -1;
            if (b.order !== null) return 1;
            return a.name.localeCompare(b.name);
          });

          setOrderedItems(sorted);
          console.log(
            `✅ OrderSelector: Loaded ${type}s for ordering:`,
            sorted.length,
          );
        } else {
          console.warn(`⚠️ OrderSelector: No ${type} data received from API`);
          setOrderedItems([]);
        }
      } catch (err) {
        console.error(`❌ OrderSelector: Failed to fetch ${type}s:`, err);
        if (err.response?.status === 404) {
          console.warn(`⚠️ OrderSelector: ${type}s endpoint not found`);
          setOrderedItems([]);
        } else {
          showWarningToast(`Failed to load ${type}s. Please refresh the page.`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (userId && (type !== "subcategory" || categoryId)) {
      fetchItems();
    } else {
      console.warn(`⚠️ OrderSelector: Missing required params for ${type}`);
    }
  }, [userId, parentId, parentType, categoryId, type, config, currentLanguage]);

  const { numberedItems, unnumberedItems } = useMemo(() => {
    const numbered = orderedItems
      .filter((item) => item.order !== null && item.order !== undefined)
      .sort((a, b) => a.order - b.order);
    const unnumbered = orderedItems
      .filter((item) => item.order === null || item.order === undefined)
      .sort((a, b) => a.name.localeCompare(b.name));
    return { numberedItems: numbered, unnumberedItems: unnumbered };
  }, [orderedItems]);

  const filteredNumbered = useMemo(() => {
    if (!searchQuery.trim()) return numberedItems;
    const query = searchQuery.toLowerCase();
    return numberedItems.filter((item) =>
      item.name.toLowerCase().includes(query),
    );
  }, [numberedItems, searchQuery]);

  const filteredUnnumbered = useMemo(() => {
    if (!searchQuery.trim()) return unnumberedItems;
    const query = searchQuery.toLowerCase();
    return unnumberedItems.filter((item) =>
      item.name.toLowerCase().includes(query),
    );
  }, [unnumberedItems, searchQuery]);

  const handleSortAlphabetically = () => {
    const sorted = [...orderedItems].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const reordered = sorted.map((item, index) => ({
      ...item,
      order: item.order !== null && item.order !== undefined ? index + 1 : null,
    }));
    setOrderedItems(reordered);
  };

  const handleCardClick = (clickedId) => {
    setOrderedItems((prev) => {
      const clickedItem = prev.find((item) => item.id === clickedId);
      if (!clickedItem) return prev;

      if (clickedItem.order !== null && clickedItem.order !== undefined) {
        const removedOrder = clickedItem.order;
        return prev.map((item) => {
          if (item.id === clickedId) {
            return { ...item, order: null };
          }
          if (item.order !== null && item.order > removedOrder) {
            return { ...item, order: item.order - 1 };
          }
          return item;
        });
      } else {
        const maxOrder = Math.max(
          0,
          ...prev.filter((i) => i.order !== null).map((i) => i.order),
        );
        return prev.map((item) => {
          if (item.id === clickedId) {
            return { ...item, order: maxOrder + 1 };
          }
          return item;
        });
      }
    });
  };

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (draggedId && draggedId !== id) {
      const draggedItem = orderedItems.find((i) => i.id === draggedId);
      const overItem = orderedItems.find((i) => i.id === id);
      if (draggedItem?.order !== null && overItem?.order !== null) {
        setDragOverId(id);
      }
    }
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }

    setOrderedItems((prev) => {
      const draggedItem = prev.find((i) => i.id === draggedId);
      const targetItem = prev.find((i) => i.id === targetId);

      if (
        !draggedItem ||
        !targetItem ||
        draggedItem.order === null ||
        targetItem.order === null
      ) {
        return prev;
      }

      return prev.map((item) => {
        if (item.id === draggedId) {
          return { ...item, order: targetItem.order };
        }
        if (item.id === targetId) {
          return { ...item, order: draggedItem.order };
        }
        return item;
      });
    });

    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = numberedItems.map((item) => ({
        [config.idKey]: item.id,
        order: item.order,
      }));

      console.log(`Saving ${type} order:`, payload);

      const requestData = {
        userId,
        [config.dataKey]: payload,
        buttonId: buttonId || null,
      };

      // Add additional params based on type
      if (type === "location" && parentId && parentType) {
        requestData.parentId = parentId;
        requestData.parentType = parentType;
      } else if (type === "subcategory" && categoryId) {
        requestData.categoryId = categoryId;
      }

      console.log(`Saving ${type} order with buttonId:`, buttonId);
      const response = await axios.post(config.saveEndpoint, requestData);

      if (response.data.success) {
        showSuccessToast("Order saved successfully!");
        console.log(`${type} order saved successfully`);
      } else {
        throw new Error(response.data.message || "Failed to save order");
      }
    } catch (error) {
      console.error("Failed to save order:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save order. Please try again.";
      showWarningToast(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Inline styles
  const styles = {
    container: {
      width: "100%",
      maxWidth: "960px",
      margin: "0 auto",
      padding: "32px 24px",
      fontFamily:
        "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      backgroundColor: "transparent", // Changed from #f8fafc to transparent
      minHeight: "auto", // Changed from 100vh to auto since main handles this
      position: "relative", // Added to prevent positioning issues
    },
    header: { marginBottom: "32px", textAlign: "center" },
    title: {
      fontSize: "32px",
      fontWeight: 700,
      color: "#0f172a",
      marginBottom: "8px",
      margin: 0,
      letterSpacing: "-0.5px",
    },
    subtitle: {
      fontSize: "15px",
      color: "#64748b",
      margin: "12px 0 0 0",
      lineHeight: 1.5,
    },
    controlsRow: {
      display: "flex",
      gap: "12px",
      marginBottom: "24px",
      flexWrap: "wrap",
    },
    searchContainer: { position: "relative", flex: 1, minWidth: "240px" },
    searchIcon: {
      position: "absolute",
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      width: "20px",
      height: "20px",
      color: "#94a3b8",
    },
    searchInput: {
      width: "100%",
      padding: "14px 20px 14px 48px",
      height: "100%",
      border: "2px solid #e2e8f0",
      borderRadius: "14px",
      fontSize: "15px",
      outline: "none",
      backgroundColor: "#ffffff",
      boxSizing: "border-box",
      transition: "all 0.2s ease",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    sortButton: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "14px 24px",
      background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
      border: "2px solid #e2e8f0",
      borderRadius: "14px",
      fontSize: "15px",
      fontWeight: 600,
      color: "#475569",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    gridContainer: {
      borderRadius: "20px",
      padding: "24px",
      backgroundColor: "#ffffff",
      marginBottom: "24px",
      boxShadow:
        "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)",
      border: "1px solid #e2e8f0",
      minHeight: "200px", // Added to prevent layout shifts
    },
    sectionLabel: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      fontSize: "13px",
      fontWeight: 700,
      color: "#64748b",
      marginBottom: "16px",
      textTransform: "uppercase",
      letterSpacing: "0.8px",
    },
    sectionCount: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "24px",
      height: "24px",
      padding: "0 8px",
      backgroundColor: "#f1f5f9",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: 700,
      color: "#475569",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
      gap: "16px",
      minHeight: "100px", // Added to prevent layout shifts when empty
    },
    card: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px 16px",
      borderRadius: "16px",
      backgroundColor: "#ffffff",
      border: "2px solid #e2e8f0",
      cursor: "pointer",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      userSelect: "none",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    cardNumbered: {
      border: `2px solid ${primaryColor}`,
      background: `linear-gradient(135deg, ${primaryColor}15 0%, ${primaryColor}10 100%)`,
      boxShadow: `0 4px 12px ${primaryColor}26`,
    },
    cardDragging: {
      opacity: 0.6,
      transform: "scale(0.95) rotate(2deg)",
      boxShadow: `0 20px 40px ${primaryColor}4d`,
    },
    cardDragOver: {
      border: `2px dashed ${primaryColor}`,
      backgroundColor: `${primaryColor}10`,
      transform: "scale(1.03)",
      boxShadow: `0 8px 24px ${primaryColor}33`,
    },
    orderBadge: {
      position: "absolute",
      top: "-12px",
      left: "-12px",
      minWidth: "32px",
      height: "32px",
      padding: "0 10px",
      background: primaryColor,
      color: "#fff",
      borderRadius: "16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "13px",
      fontWeight: 800,
      boxShadow: `0 4px 12px ${primaryColor}66`,
      border: "3px solid #ffffff",
    },
    dragHandle: {
      position: "absolute",
      top: "10px",
      right: "10px",
      color: `${primaryColor}80`,
      opacity: 0,
      transition: "all 0.2s ease",
      padding: "4px",
      borderRadius: "6px",
    },
    photoContainer: {
      width: "64px",
      height: "64px",
      marginBottom: "14px",
      borderRadius: "14px",
      overflow: "hidden",
      backgroundColor: "#f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    },
    photo: { width: "100%", height: "100%", objectFit: "cover" },
    photoPlaceholder: {
      width: "100%",
      height: "100%",
      background: `linear-gradient(135deg, ${primaryColor}20 0%, ${primaryColor}15 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    itemName: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#1e293b",
      textAlign: "center",
      lineHeight: 1.4,
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
    },
    emptyState: { textAlign: "center", padding: "64px 24px", color: "#94a3b8" },
    emptyStateIcon: {
      width: "64px",
      height: "64px",
      margin: "0 auto 16px",
      opacity: 0.5,
    },
    summaryContainer: {
      borderRadius: "20px",
      padding: "20px 24px",
      background: "linear-gradient(135deg, #fefefe 0%, #f8fafc 100%)",
      marginBottom: "24px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
    },
    summaryTitle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: "14px",
      fontWeight: 700,
      color: "#374151",
      marginBottom: "16px",
    },
    summaryBadge: {
      fontSize: "12px",
      fontWeight: 600,
      color: primaryColor,
      backgroundColor: `${primaryColor}15`,
      padding: "4px 12px",
      borderRadius: "20px",
    },
    summaryChips: { display: "flex", flexWrap: "wrap", gap: "10px" },
    summaryChip: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 16px 8px 8px",
      backgroundColor: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: "24px",
      fontSize: "13px",
      transition: "all 0.2s ease",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
    summaryChipBadge: {
      width: "24px",
      height: "24px",
      background: primaryColor,
      color: "#fff",
      borderRadius: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "11px",
      fontWeight: "800",
    },
    saveButton: {
      width: "100%",
      padding: "18px 32px",
      background: primaryColor,
      color: "#fff",
      border: "none",
      borderRadius: "16px",
      fontSize: "16px",
      fontWeight: 700,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      boxShadow: `0 4px 14px ${primaryColor}66`,
      letterSpacing: "0.3px",
    },
    saveButtonDisabled: {
      background: "linear-gradient(135deg, #cbd5e1 0%, #94a3b8 100%)",
      cursor: "not-allowed",
      boxShadow: "none",
    },
    divider: {
      height: "2px",
      background:
        "linear-gradient(90deg, transparent 0%, #e2e8f0 50%, transparent 100%)",
      margin: "28px 0",
      borderRadius: "1px",
    },
    clickHint: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "11px",
      fontWeight: 500,
      color: "#94a3b8",
      marginTop: "8px",
      padding: "4px 10px",
      backgroundColor: "#f8fafc",
      borderRadius: "8px",
    },
    loadingContainer: { textAlign: "center", padding: "100px 20px" },
    loadingSpinner: {
      width: "50px",
      height: "50px",
      border: "3px solid #f3f3f3",
      borderTop: `3px solid ${primaryColor}`,
      borderRadius: "50%",
      margin: "0 auto 20px",
      animation: "spin 1s linear infinite",
    },
  };

  // SVG Icon Components
  const SearchIcon = () => (
    <svg
      style={styles.searchIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );

  const SortIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 6h18M3 12h12M3 18h6" />
    </svg>
  );

  const GripIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="8" cy="5" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="8" cy="19" r="2" />
      <circle cx="16" cy="5" r="2" />
      <circle cx="16" cy="12" r="2" />
      <circle cx="16" cy="19" r="2" />
    </svg>
  );

  const MapPinIcon = ({ size = 28, color = primaryColor }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );

  const CategoryIcon = ({ size = 28, color = primaryColor }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );

  const SubCategoryIcon = ({ size = 28, color = primaryColor }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
    >
      <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />
    </svg>
  );

  const SaveIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17,21 17,13 7,13 7,21" />
      <polyline points="7,3 7,8 15,8" />
    </svg>
  );

  const CheckIcon = () => (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20,6 9,17 4,12" />
    </svg>
  );

  // Get appropriate icon component
  const getIconComponent = (iconType) => {
    switch (iconType) {
      case "category":
        return CategoryIcon;
      case "subcategory":
        return SubCategoryIcon;
      default:
        return MapPinIcon;
    }
  };

  const IconComponent = getIconComponent(config.icon);

  const renderCard = (item, isNumbered) => {
    const isDragging = draggedId === item.id;
    const isDragOver = dragOverId === item.id;

    const cardStyle = {
      ...styles.card,
      ...(isNumbered ? styles.cardNumbered : {}),
      ...(isDragging ? styles.cardDragging : {}),
      ...(isDragOver ? styles.cardDragOver : {}),
    };

    return (
      <div
        key={item.id}
        style={cardStyle}
        onClick={() => handleCardClick(item.id)}
        draggable={isNumbered}
        onDragStart={(e) => isNumbered && handleDragStart(e, item.id)}
        onDragOver={(e) => handleDragOver(e, item.id)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, item.id)}
        onDragEnd={handleDragEnd}
        onMouseEnter={(e) => {
          const handle = e.currentTarget.querySelector(".drag-handle");
          if (handle) handle.style.opacity = "1";
          if (!isDragging && !isDragOver) {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = isNumbered
              ? `0 12px 28px ${primaryColor}40`
              : "0 12px 28px rgba(0,0,0,0.12)";
          }
        }}
        onMouseLeave={(e) => {
          const handle = e.currentTarget.querySelector(".drag-handle");
          if (handle) handle.style.opacity = "0";
          if (!isDragging && !isDragOver) {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = isNumbered
              ? `0 4px 12px ${primaryColor}26`
              : "0 1px 3px rgba(0,0,0,0.04)";
          }
        }}
      >
        {isNumbered && <div style={styles.orderBadge}>{item.order}</div>}

        {isNumbered && (
          <div className="drag-handle" style={styles.dragHandle}>
            <GripIcon />
          </div>
        )}

        {type === "location" && item.photo ? (
          <div style={styles.photoContainer}>
            <img src={item.photo} alt={item.name} style={styles.photo} />
          </div>
        ) : type === "location" ? (
          <div style={styles.photoContainer}>
            <div style={styles.photoPlaceholder}>
              <IconComponent />
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: "14px" }}>
            <IconComponent />
          </div>
        )}

        <span style={styles.itemName}>{item.name}</span>

        {isNumbered && (
          <div style={styles.clickHint}>
            <CheckIcon /> Selected
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <main
        style={{
          minHeight: "100vh",
          backgroundColor: "#f8fafc",
          padding: "10px 16px",
        }}
      >
        <div className="order-selector-container" style={styles.container}>
          <style>{`
            @keyframes spin { 
              from { transform: rotate(0deg); } 
              to { transform: rotate(360deg); } 
            } 
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
            
            /* Prevent layout shifts */
            .order-selector-container * {
              box-sizing: border-box;
            }
            
            .order-selector-container {
              position: relative;
              width: 100%;
            }
          `}</style>
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p style={{ color: "#64748b", fontSize: "16px" }}>
              {config.loadingMessage}
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      style={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        padding: "60px 16px",
      }}
    >
      <div className="order-selector-container" style={styles.container}>
          <style>{`
            @keyframes spin { 
              from { transform: rotate(0deg); } 
              to { transform: rotate(360deg); } 
            } 
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
            
            /* Prevent layout shifts */
            .order-selector-container * {
              box-sizing: border-box;
            }
            
            .order-selector-container {
              position: relative;
              width: 100%;
            }
          `}</style>

          <div style={styles.header}>
            <h2 style={styles.title}>{config.title}</h2>
            <p style={styles.subtitle}>
              Click to select or deselect. Drag selected items to reorder.
            </p>
          </div>

          <div style={styles.controlsRow}>
            <div style={styles.searchContainer}>
              <SearchIcon />
              <input
                type="text"
                placeholder={config.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={styles.searchInput}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.boxShadow = `0 0 0 4px ${primaryColor}1a`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2e8f0";
                  e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
                }}
              />
            </div>
            <button
              style={styles.sortButton}
              onClick={handleSortAlphabetically}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = primaryColor;
                e.currentTarget.style.color = primaryColor;
                e.currentTarget.style.boxShadow = `0 4px 12px ${primaryColor}26`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e2e8f0";
                e.currentTarget.style.color = "#475569";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
              }}
            >
              <SortIcon />
              Sort A-Z
            </button>
          </div>

          <div style={styles.gridContainer}>
            {filteredNumbered.length > 0 && (
              <>
                <div style={styles.sectionLabel}>
                  <IconComponent size={18} />
                  {config.selectedLabel}
                  <span style={styles.sectionCount}>
                    {numberedItems.length}
                  </span>
                </div>
                <div style={styles.grid}>
                  {filteredNumbered.map((item) => renderCard(item, true))}
                </div>
              </>
            )}

            {filteredNumbered.length > 0 && filteredUnnumbered.length > 0 && (
              <div style={styles.divider} />
            )}

            {filteredUnnumbered.length > 0 && (
              <>
                <div style={styles.sectionLabel}>
                  <span style={{ opacity: 0.6 }}>
                    <IconComponent size={18} color="#94a3b8" />
                  </span>
                  {config.availableLabel}
                  <span style={styles.sectionCount}>
                    {unnumberedItems.length}
                  </span>
                </div>
                <div style={styles.grid}>
                  {filteredUnnumbered.map((item) => renderCard(item, false))}
                </div>
              </>
            )}

            {filteredNumbered.length === 0 &&
              filteredUnnumbered.length === 0 && (
                <div style={styles.emptyState}>
                  <div style={styles.emptyStateIcon}>
                    <IconComponent size={64} color="#cbd5e1" />
                  </div>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: 500 }}>
                    {searchQuery ? `No ${type}s found` : config.emptyMessage}
                  </p>
                  <p style={{ margin: "8px 0 0", fontSize: "14px" }}>
                    {searchQuery
                      ? "Try a different search term"
                      : config.emptyHint}
                  </p>
                  {!searchQuery && type === "location" && (
                    <p
                      style={{
                        margin: "8px 0 0",
                        fontSize: "12px",
                        color: "#94a3b8",
                      }}
                    >
                      Current userId: {userId}
                    </p>
                  )}
                </div>
              )}
          </div>

          {numberedItems.length > 0 && (
            <div style={styles.summaryContainer}>
              <div style={styles.summaryTitle}>
                <span>Current Order</span>
                <span style={styles.summaryBadge}>
                  {numberedItems.length} selected
                </span>
              </div>
              <div style={styles.summaryChips}>
                {numberedItems.map((item) => (
                  <div
                    key={item.id}
                    style={styles.summaryChip}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = primaryColor;
                      e.currentTarget.style.boxShadow = `0 2px 8px ${primaryColor}26`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e2e8f0";
                      e.currentTarget.style.boxShadow =
                        "0 1px 2px rgba(0,0,0,0.04)";
                    }}
                  >
                    <span style={styles.summaryChipBadge}>{item.order}</span>
                    <span style={{ fontWeight: 600, color: "#374151" }}>
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={isSaving || numberedItems.length === 0}
            style={{
              ...styles.saveButton,
              ...(isSaving || numberedItems.length === 0
                ? styles.saveButtonDisabled
                : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSaving && numberedItems.length > 0) {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = `0 8px 24px ${primaryColor}80`;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving && numberedItems.length > 0) {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 4px 14px ${primaryColor}66`;
              }
            }}
          >
            {isSaving ? (
              <>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  style={{ animation: "spin 1s linear infinite" }}
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Saving...
              </>
            ) : (
              <>
                <SaveIcon />
                Save Order
              </>
            )}
          </button>
        </div>
      
    </main>
  );
}
